import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import styles from './AboutPage.module.css';

const AboutPage = () => {
  usePageMeta({
    title: 'About',
    path: '/about',
    description: 'Anti-clickbait, anti-partisan news — truth over theater, powered by AI and independent sources.',
  });
  return (
    <div className={`more-page ${styles.aboutPage}`}>
      <div className="container">
        <div className={`more-page-header ${styles.pageHeader}`}>
          <h1>About DarkNews</h1>
          <p className={styles.subtitle}>
            Anti-establishment, anti-partisan, anti-clickbait — truth over theater, powered by AI
          </p>
        </div>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2>Our Mission</h2>
            <p>
              DarkNews exists to help readers pursue journalistic truth in a media economy built
              for attention and power. We harvest from diverse RSS sources — especially independent
              and underrepresented voices — and use AI for search, context, and research. We reject
              Left-versus-Right “balance” as a substitute for truth. The useful lens is who holds
              power, who owns the story, and what the evidence actually supports — not which
              partisan jersey a masthead wears.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Our Values</h2>
            <div className={styles.valuesGrid}>
              <div className={styles.valueCard}>
                <h3>Anti-Establishment</h3>
                <p>
                  We elevate independent and underrepresented sources that concentrated ownership
                  and mainstream agenda-setting overlook — because capture is structural, not
                  cosmetic. No institution is the default voice of record.
                </p>
              </div>
              <div className={styles.valueCard}>
                <h3>Anti-Partisan</h3>
                <p>
                  We refuse Left/Right spectra as the product. Partisan theater distracts from
                  top-versus-bottom power. Multi-source reading means structural perspective and
                  evidence — not false equivalence.
                </p>
              </div>
              <div className={styles.valueCard}>
                <h3>Anti-Clickbait</h3>
                <p>
                  Substance over sensationalism. We optimize for information that informs, not
                  headlines engineered to manipulate.
                </p>
              </div>
              <div className={styles.valueCard}>
                <h3>Independence, not neutrality theater</h3>
                <p>
                  Independence from concentrated ownership is a virtue. Pretending the reporter has
                  no stance is not the same virtue. We would rather show our method than perform
                  amoral “both sides.”
                </p>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2>How we differ from spectrum aggregators</h2>
            <p>
              Some platforms sell “balance” by scoring outlets on a Liberal–Conservative bar and
              letting you diet across the spectrum. That map treats reality as culture-war
              affiliation. DarkNews does not. We do not grade journalism by party costume. We ask
              structural questions — independence, institution type, incentives, evidence — and we
              give you tools to harvest, search, and research without a morale compass labeled Left
              or Right.
            </p>
            <ul>
              <li>
                Independent-forward harvest, not a shopping cart of partisan brands
              </li>
              <li>
                Structural source context (<code>sourceType</code>), not ideology chips
              </li>
              <li>Semantic search and an entity graph across the archive</li>
              <li>Research workspace for digging — agency as capacity, not team identity</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2>How We Work</h2>
            <h3>Harvest</h3>
            <p>
              We continuously collect articles from a curated set of RSS feeds — established desks
              and independents alike. Live scale is on the{' '}
              <Link to="/">home page</Link> and <Link to="/stats">feed statistics</Link>. Publishers
              who do not want to be harvested can ask on our{' '}
              <Link to="/copyright">Copyright / DMCA</Link> page; we will remove the feed.
            </p>
            <h3>Search and research</h3>
            <p>
              AI is for retrieval, pattern-finding, and research — not for painting culture-war
              bias bars. The <Link to="/desk">Desk</Link> is a live intelligence board;{' '}
              <Link to="/web">Web</Link> is the story graph; <Link to="/feed">Feed</Link> is the
              searchable archive; <Link to="/research">Research</Link> is a private workspace for
              signed-in readers.
            </p>
            <h3>What you read here</h3>
            <p>
              Public article pages show the headline and RSS description, then send you to the
              original. Full scraped text stays off the page — analytics only. That is both a
              legal posture and a product choice: we are an index and a research tool, not a
              reprint mill.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Our Technology</h2>
            <div className={styles.techStack}>
              <div className={styles.techItem}>
                <h4>Frontend</h4>
                <p>React — Desk, Web graph, Feed, Research</p>
              </div>
              <div className={styles.techItem}>
                <h4>API</h4>
                <p>Spring Boot, Mongo articles, Postgres graph and search</p>
              </div>
              <div className={styles.techItem}>
                <h4>Harvest</h4>
                <p>DGX pipeline: extract, embed, push to prod ingest</p>
              </div>
              <div className={styles.techItem}>
                <h4>Search</h4>
                <p>pgvector semantic retrieval plus graph neighborhood</p>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2>Privacy and method</h2>
            <p>
              Basic reading does not require an account. Sign-in uses OAuth. We document how we
              harvest and score rather than hiding behind opaque “bias” meters. See the{' '}
              <Link to="/privacy">Privacy Policy</Link>, <Link to="/terms">Terms</Link>, and{' '}
              <Link to="/copyright">Copyright / DMCA</Link> page.
            </p>
          </section>

          <section className={styles.section}>
            <h2>Contact</h2>
            <p>
              Questions and suggestions: <Link to="/contact">Contact</Link>. Feed removal:{' '}
              <Link to="/copyright">Copyright / DMCA</Link>.
            </p>
            <div className={styles.contactInfo}>
              <p>
                <strong>General:</strong> <a href="mailto:info@darknews.ai">info@darknews.ai</a>
              </p>
              <p>
                <strong>Support:</strong> <a href="mailto:support@darknews.ai">support@darknews.ai</a>
              </p>
              <p>
                <strong>Copyright:</strong>{' '}
                <a href="mailto:copyright@darknews.ai">copyright@darknews.ai</a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
